import { html, render } from "lit-html";
import { encodeSVGPath, SVGPathData } from "svg-pathdata";
import { SVGCommand } from "svg-pathdata/lib/types";

import styles from "./CodeDiagnosticHighlights.module.css";
import { LineElement } from "./CodeDiagnosticHighlights/LineElements";

export function createSingleLineHighlight(numCols: number): HTMLElement {
  const root = document.createElement("div");
  root.className = styles.root;
  root.appendChild(document.createTextNode("\xa0".repeat(numCols)));
  return root;
}

/**
 * @deprecated
 */
export function createCharHighlightOLD(_numRows: number, _numCols: number): HTMLElement {
  const template = html`<div class="${styles.root}">&nbsp;</div>`;

  const root = document.createElement("div");
  render(template, root);
  return root;
}

export interface CellSize {
  widthPx: number;
  heightPx: number;
}

export const OUTLINE_SHAPE_PADDING = 4;

export function outlineShape(
  cellSize: CellSize,
  lines: LineElement[],
  offsetX: number,
  offsetY: number
): Element {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

  // Toggle this to `true` to show helpful rect visualizations
  const DEBUG_SHOW_RECTS = false as boolean;
  if (DEBUG_SHOW_RECTS) {
    let y: number = 0;
    for (const line of lines) {
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.classList.add(styles.outlineshape_rect);
      rect.setAttribute("x", `${offsetX + cellSize.widthPx * line.startCol}`);
      rect.setAttribute("y", `${offsetY + y}`);
      rect.setAttribute("width", `${cellSize.widthPx * line.width}`);
      rect.setAttribute("height", `${cellSize.heightPx}`);
      svg.appendChild(rect);

      y += cellSize.heightPx;
    }
  }

  const pathCommands: SVGCommand[] = [];

  pathCommands.push({
    type: SVGPathData.MOVE_TO,
    relative: false,
    x: offsetX + cellSize.widthPx * lines[0].startCol,
    y: offsetY - OUTLINE_SHAPE_PADDING
  });

  const rightMidIndex = getRightMidIndex(lines);
  const leftMidIndex = getLeftMidIndex(lines);

  rightTopPathCommands(cellSize, lines, pathCommands, rightMidIndex, offsetX, offsetY);

  pathCommands.push({
    type: SVGPathData.VERT_LINE_TO,
    relative: false,
    y: offsetY + rightMidIndex * cellSize.heightPx + cellSize.heightPx
  });
  pathCommands.push({
    type: SVGPathData.ARC,
    relative: false,
    x:
      offsetX +
      cellSize.widthPx * lines[rightMidIndex].startCol +
      cellSize.widthPx * lines[rightMidIndex].width,
    y: offsetY + rightMidIndex * cellSize.heightPx + cellSize.heightPx + OUTLINE_SHAPE_PADDING,
    rX: OUTLINE_SHAPE_PADDING,
    rY: OUTLINE_SHAPE_PADDING,
    xRot: 0,
    sweepFlag: 1,
    lArcFlag: 0
  });

  rightBottomPathCommands(cellSize, lines, pathCommands, rightMidIndex, offsetX, offsetY);
  leftBottomPathCommands(cellSize, lines, pathCommands, leftMidIndex, offsetX, offsetY);

  pathCommands.push({
    type: SVGPathData.VERT_LINE_TO,
    relative: false,
    y: offsetY + leftMidIndex * cellSize.heightPx
  });
  pathCommands.push({
    type: SVGPathData.ARC,
    relative: false,
    x: offsetX + cellSize.widthPx * lines[leftMidIndex].startCol,
    y: offsetY - OUTLINE_SHAPE_PADDING + leftMidIndex * cellSize.heightPx,
    rX: OUTLINE_SHAPE_PADDING,
    rY: OUTLINE_SHAPE_PADDING,
    xRot: 0,
    sweepFlag: 1,
    lArcFlag: 0
  });

  leftTopPathCommands(cellSize, lines, pathCommands, leftMidIndex, offsetX, offsetY);

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.classList.add(styles.outlineshape_path);
  path.setAttribute("d", encodeSVGPath(pathCommands));
  svg.appendChild(path);

  svg.setAttribute(
    "width",
    "" +
      (offsetX +
        cellSize.widthPx * (lines[rightMidIndex].startCol + lines[rightMidIndex].width) +
        OUTLINE_SHAPE_PADDING)
  );
  svg.setAttribute(
    "height",
    "" + (offsetY + lines.length * cellSize.heightPx + OUTLINE_SHAPE_PADDING)
  );

  return svg;
}

function getRightMidIndex(lines: LineElement[]): number {
  let i = 0;
  for (i = 0; i < lines.length - 1; ++i) {
    if (lines[i + 1].startCol + lines[i + 1].width < lines[i].startCol + lines[i].width) {
      return i;
    }
  }
  return i;
}

function getLeftMidIndex(lines: LineElement[]): number {
  let i = lines.length - 1;
  for (i = lines.length - 1; i > 0; --i) {
    if (lines[i - 1].startCol > lines[i].startCol) {
      return i;
    }
  }
  return i;
}

function rightTopPathCommands(
  cellSize: CellSize,
  lines: LineElement[],
  pathCommands: SVGCommand[],
  midIndex: number,
  offsetX: number,
  offsetY: number
): void {
  for (let i = 0; i <= midIndex; ++i) {
    const line = lines[i];
    if (
      i === 0 ||
      lines[i - 1].startCol + lines[i - 1].width < lines[i].startCol + lines[i].width
    ) {
      pathCommands.push({
        type: SVGPathData.HORIZ_LINE_TO,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol + cellSize.widthPx * line.width
      });
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x:
          offsetX +
          OUTLINE_SHAPE_PADDING +
          cellSize.widthPx * line.startCol +
          cellSize.widthPx * line.width,
        y: offsetY + i * cellSize.heightPx,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 1,
        lArcFlag: 0
      });
    }
    pathCommands.push({
      type: SVGPathData.VERT_LINE_TO,
      relative: false,
      y: offsetY + i * cellSize.heightPx + cellSize.heightPx - 2 * OUTLINE_SHAPE_PADDING
    });
    if (
      i < lines.length - 1 &&
      lines[i + 1].startCol + lines[i + 1].width > lines[i].startCol + lines[i].width
    ) {
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x:
          offsetX +
          2 * OUTLINE_SHAPE_PADDING +
          cellSize.widthPx * line.startCol +
          cellSize.widthPx * line.width,
        y: offsetY + i * cellSize.heightPx + cellSize.heightPx - OUTLINE_SHAPE_PADDING,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 0,
        lArcFlag: 0
      });
    }
  }
}

function rightBottomPathCommands(
  cellSize: CellSize,
  lines: LineElement[],
  pathCommands: SVGCommand[],
  midIndex: number,
  offsetX: number,
  offsetY: number
): void {
  for (let i = midIndex + 1; i < lines.length; ++i) {
    const line = lines[i];
    if (lines[i - 1].startCol + lines[i - 1].width > lines[i].startCol + lines[i].width) {
      pathCommands.push({
        type: SVGPathData.HORIZ_LINE_TO,
        relative: false,
        x:
          offsetX +
          2 * OUTLINE_SHAPE_PADDING +
          cellSize.widthPx * line.startCol +
          cellSize.widthPx * line.width
      });
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x:
          offsetX +
          OUTLINE_SHAPE_PADDING +
          cellSize.widthPx * line.startCol +
          cellSize.widthPx * line.width,
        y: offsetY + i * cellSize.heightPx + 2 * OUTLINE_SHAPE_PADDING,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 0,
        lArcFlag: 0
      });
    }
    pathCommands.push({
      type: SVGPathData.VERT_LINE_TO,
      relative: false,
      y: offsetY + i * cellSize.heightPx + cellSize.heightPx
    });
    if (
      i === lines.length - 1 ||
      lines[i + 1].startCol + lines[i + 1].width < lines[i].startCol + lines[i].width
    ) {
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol + cellSize.widthPx * line.width,
        y: offsetY + i * cellSize.heightPx + cellSize.heightPx + OUTLINE_SHAPE_PADDING,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 1,
        lArcFlag: 0
      });
    }
  }
}

function leftBottomPathCommands(
  cellSize: CellSize,
  lines: LineElement[],
  pathCommands: SVGCommand[],
  midIndex: number,
  offsetX: number,
  offsetY: number
): void {
  for (let i = lines.length - 1; i >= midIndex; --i) {
    const line = lines[i];
    if (i === lines.length - 1 || lines[i + 1].startCol > lines[i].startCol) {
      pathCommands.push({
        type: SVGPathData.HORIZ_LINE_TO,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol
      });
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol - OUTLINE_SHAPE_PADDING,
        y: offsetY + i * cellSize.heightPx + cellSize.heightPx,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 1,
        lArcFlag: 0
      });
    }
    pathCommands.push({
      type: SVGPathData.VERT_LINE_TO,
      relative: false,
      y: offsetY + 2 * OUTLINE_SHAPE_PADDING + i * cellSize.heightPx
    });
    if (i > 0 && lines[i - 1].startCol < lines[i].startCol) {
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol - 2 * OUTLINE_SHAPE_PADDING,
        y: offsetY + OUTLINE_SHAPE_PADDING + i * cellSize.heightPx,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 0,
        lArcFlag: 0
      });
    }
  }
}

function leftTopPathCommands(
  cellSize: CellSize,
  lines: LineElement[],
  pathCommands: SVGCommand[],
  midIndex: number,
  offsetX: number,
  offsetY: number
): void {
  for (let i = midIndex - 1; i >= 0; --i) {
    const line = lines[i];
    if (lines[i + 1].startCol < lines[i].startCol) {
      pathCommands.push({
        type: SVGPathData.HORIZ_LINE_TO,
        relative: false,
        x: offsetX - 2 * OUTLINE_SHAPE_PADDING + cellSize.widthPx * line.startCol
      });
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol - OUTLINE_SHAPE_PADDING,
        y: offsetY + i * cellSize.heightPx + cellSize.heightPx - 2 * OUTLINE_SHAPE_PADDING,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 0,
        lArcFlag: 0
      });
    }
    pathCommands.push({
      type: SVGPathData.VERT_LINE_TO,
      relative: false,
      y: offsetY + i * cellSize.heightPx
    });
    if (i === 0 || lines[i - 1].startCol > lines[i].startCol) {
      pathCommands.push({
        type: SVGPathData.ARC,
        relative: false,
        x: offsetX + cellSize.widthPx * line.startCol,
        y: offsetY + i * cellSize.heightPx - OUTLINE_SHAPE_PADDING,
        rX: OUTLINE_SHAPE_PADDING,
        rY: OUTLINE_SHAPE_PADDING,
        xRot: 0,
        sweepFlag: 1,
        lArcFlag: 0
      });
    }
  }
}
